// Domain-driven branding.
//
// One source of truth for logos, favicons and product names. The brand is
// resolved at first paint based on the host (and an optional ?brand=...
// query override for QA). React reads it via the useBrand hook; the inline
// <head> bootstrap in index.html applies favicon + title before React
// mounts to avoid a flash of the wrong brand.

export type BrandKey = 'cherry' | 'fruitmarkets';

export type BrandAssets = {
  /** Long product name. Used in <title>, marketing copy, alts. */
  name: string;
  /** Short name. Useful for tight headers. */
  shortName: string;
  /** Full horizontal logo over a light background. */
  fullLogoLight: string;
  /** Full horizontal logo over a dark background (white version). */
  fullLogoDark: string;
  /** Symbol-only logo over a light background. */
  iconLight: string;
  /** Symbol-only logo over a dark background. */
  iconDark: string;
  /** Symbol used in the collapsed sidebar (light bg). May equal iconLight. */
  collapsedLight: string;
  /** Symbol used in the collapsed sidebar (dark bg). */
  collapsedDark: string;
  /** Path served as the favicon. */
  favicon: string;
  /** Images displayed in rotation on the login screen. */
  loginBackgrounds: string[];
};

export const BRANDS: Record<BrandKey, BrandAssets> = {
  cherry: {
    name: 'CherryMarket',
    shortName: 'Cherry',
    fullLogoLight: '/full-logo-by-fruitmarkets-cropped.png',
    fullLogoDark: '/white-full-logo-by-fruitmarkets-cropped.png',
    iconLight: '/icon.png',
    iconDark: '/white-icon.png',
    collapsedLight: '/collapsed-letters.png',
    collapsedDark: '/white-collapsed-logo.png',
    favicon: '/icon.png',
    loginBackgrounds: ['/cherry.jpg'],
  },
  fruitmarkets: {
    name: 'FruitMarkets',
    shortName: 'FruitMarkets',
    // Light mode uses the colored logo (symbol in pink/green/purple, black wordmark).
    // Dark mode swaps the wordmark to white but keeps the colored symbol.
    fullLogoLight: '/fruitmarkets/logo-horizontal.png',
    fullLogoDark: '/fruitmarkets/logo-horizontal-white.png',
    iconLight: '/fruitmarkets/symbol.png',
    iconDark: '/fruitmarkets/symbol.png',
    collapsedLight: '/fruitmarkets/symbol.png',
    collapsedDark: '/fruitmarkets/symbol.png',
    favicon: '/fruitmarkets/symbol.png',
    loginBackgrounds: ['/kiwi.jpg', '/orange.jpg', '/cherry.jpg'],
  },
};

/** Default brand when nothing matches (localhost, preview deploys, etc). */
export const DEFAULT_BRAND: BrandKey = 'cherry';

/** Host → brand. First match wins. */
const HOST_TO_BRAND: Array<[RegExp, BrandKey]> = [
  [/(^|\.)fruitmarkets\.com$/i, 'fruitmarkets'],
  [/(^|\.)cherrymarket\.cl$/i, 'cherry'],
];

/** Query string used to force a brand for QA (e.g. ?brand=fruitmarkets). */
const BRAND_QUERY_PARAM = 'brand';

function readBrandFromQuery(search: string): BrandKey | null {
  if (!search) return null;
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );
  const value = params.get(BRAND_QUERY_PARAM);
  if (!value) return null;
  const normalized = value.toLowerCase();
  return normalized in BRANDS ? (normalized as BrandKey) : null;
}

function readBrandFromHost(host: string): BrandKey | null {
  for (const [re, brand] of HOST_TO_BRAND) {
    if (re.test(host)) return brand;
  }
  return null;
}

/**
 * Resolve the active brand. Priority:
 *   1. ?brand=... in the URL (QA override).
 *   2. Hostname match (production).
 *   3. DEFAULT_BRAND.
 *
 * Safe to call during SSR (returns the default).
 */
export function detectBrandKey(
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): BrandKey {
  return readBrandFromQuery(search) ?? readBrandFromHost(host) ?? DEFAULT_BRAND;
}

/** Convenience: assets for the currently-detected brand. */
export function getBrandAssets(): BrandAssets {
  return BRANDS[detectBrandKey()];
}
